import React from 'react';

import {
  assertDataViewActionIsConstant,
  OverflowDataViewAction,
} from 'analysis-tool/data-view/DataViewContext';
import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {DATA_FRAMES_ANALYTICS_ACTIONS} from 'constants/analytics-products/DataFramesAnalyticsConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {createUnvalidatedAccessor, rowIdAccessor} from 'data-frames/util/createAccessors';
import {isValidColumnType} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import Option from 'ui-library/menus/Option';
import {JSONValue} from 'util/Types';
import getAddColumnToWorklistMenu, {
  handleSelectColumn,
  WorklistColumn,
} from 'worklists/getAddColumnToWorklistMenu';
import {areIdsValidForWorklist} from 'worklists/WorklistUtil';

import {OverflowActionRenderFn, ToolbarActionRenderParams} from '../DataFrameActionToolbar';

// The leaf types whose columns can be added to worklists.
// Child types (e.g., Sequence under Bioentity) are handled automatically by isValidColumnType.
const WORKLISTABLE_LEAF_TYPES: ReadonlyArray<LeafType> = [
  LeafType.BATCH,
  LeafType.BIOENTITY,
  LeafType.CONTAINER,
  LeafType.PLATE,
];

/** Narrows JSONValue to a non-empty string entity ID, or null. */
function extractEntityId(value: JSONValue): string | null {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return null;
}

/**
 * Extract worklistable columns from a DataFrame.
 *
 * @param dataFrame - The DataFrame to extract columns from
 * @param selectedRowIds - When non-empty, only process rows whose row IDs are in this set.
 *   An empty set means "all rows".
 */
export function extractWorklistColumns(
  dataFrame: DataFrame,
  selectedRowIds: Set<string> = new Set()
): Array<WorklistColumn> {
  const worklistColumns: Array<WorklistColumn> = [];

  const rowsToProcess =
    selectedRowIds.size > 0
      ? dataFrame.rows.filter((row) => selectedRowIds.has(rowIdAccessor(row)))
      : dataFrame.rows;

  for (const column of dataFrame.columns) {
    if (!isValidColumnType(column.type, {allowedLeafTypes: WORKLISTABLE_LEAF_TYPES})) {
      continue;
    }

    const accessor = createUnvalidatedAccessor(column);
    const entityIds: Array<string> = [];

    for (const row of rowsToProcess) {
      const cellValue = accessor(row);
      const values = column.type.isList && Array.isArray(cellValue) ? cellValue : [cellValue];
      for (const item of values) {
        const id = extractEntityId(item);
        if (id) {
          entityIds.push(id);
        }
      }
    }

    if (entityIds.length === 0) {
      continue;
    }

    const uniqueIds = Array.from(new Set(entityIds));

    worklistColumns.push({
      name: column.displayName,
      itemIds: uniqueIds,
      leafType: column.type.leafType,
    });
  }

  return worklistColumns;
}

export function getAddToWorklistAction(
  dataFrame: DataFrame,
  analyticsData: {
    dataFrameId?: string;
    actionOrigin: ActionOrigin;
  },
  selectedRowIds: Set<string>,
  isResolvingSelectedRows?: boolean,
  hasErrorLoadingSelectedRows?: boolean
): OverflowDataViewAction {
  const maxItems = BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST;
  const worklistColumns = extractWorklistColumns(dataFrame, selectedRowIds);
  const exceedsLimit = worklistColumns.some((col) => col.itemIds.length > maxItems);
  const numSelectedRows = selectedRowIds.size;

  const hasColumnWithValidIds = worklistColumns.some(
    ({itemIds}) => itemIds.length > 0 && areIdsValidForWorklist(itemIds)
  );
  const hasValidOption = !exceedsLimit && hasColumnWithValidIds;

  let helpText: string | undefined;
  if (isResolvingSelectedRows) {
    helpText = 'Loading selected rows…';
  } else if (hasErrorLoadingSelectedRows) {
    helpText = "Couldn't load some selected rows.";
  } else if (!hasValidOption) {
    if (exceedsLimit) {
      helpText = `Worklist limit is ${maxItems} items. Select fewer rows to add to a worklist.`;
    } else {
      helpText = 'No columns with worklist items found';
    }
  }

  return {
    icon: 'worklist-add',
    label:
      numSelectedRows === 0
        ? 'Add to worklist'
        : `Add ${numSelectedRows} ${pluralize(numSelectedRows, 'row')} to worklist`,
    isDisabled: isResolvingSelectedRows || hasErrorLoadingSelectedRows || !hasValidOption,
    helpText,
    onClick: () => {
      if (worklistColumns.length === 1) {
        trackEvent(DATA_FRAMES_ANALYTICS_ACTIONS.INITIATED_ADD_DATA_FRAME_COLUMN_TO_WORKLIST, {
          data_frame_id: analyticsData.dataFrameId,
          action_origin: analyticsData.actionOrigin,
          num_rows_selected: selectedRowIds.size,
          num_items: worklistColumns[0].itemIds.length,
          column_name: worklistColumns[0].name,
          num_worklistable_columns: 1,
        });
        handleSelectColumn(worklistColumns[0]);
      }
    },
    submenu:
      worklistColumns.length > 1
        ? getAddColumnToWorklistMenu({
            columns: worklistColumns,
            dropLeft: true,
            onSelect: (column) => {
              trackEvent(DATA_FRAMES_ANALYTICS_ACTIONS.INITIATED_ADD_DATA_FRAME_COLUMN_TO_WORKLIST, {
                data_frame_id: analyticsData.dataFrameId,
                action_origin: analyticsData.actionOrigin,
                num_rows_selected: selectedRowIds.size,
                num_items: column.itemIds.length,
                column_name: column.name,
                num_worklistable_columns: worklistColumns.length,
              });
            },
          })
        : undefined,
  };
}

/**
 * @deprecated TODO(BNCH-123685): Remove once uses of DataFrameTableWrapper are removed.
 */
export const renderAddToWorklistOption: OverflowActionRenderFn = (
  dataFrame: DataFrame,
  params: Pick<
    ToolbarActionRenderParams,
    | 'dataFrameId'
    | 'selectedRowIds'
    | 'actionOrigin'
    | 'isResolvingSelectedRows'
    | 'hasErrorLoadingSelectedRows'
  >
) => {
  const option = getAddToWorklistAction(
    dataFrame,
    {
      dataFrameId: params.dataFrameId,
      actionOrigin: params.actionOrigin,
    },
    params.selectedRowIds,
    params.isResolvingSelectedRows,
    params.hasErrorLoadingSelectedRows
  );
  assertDataViewActionIsConstant(option);

  return (
    <Option
      icon={option.icon}
      isDisabled={option.isDisabled}
      key="AddToWorklistOption"
      onSelect={option.onClick}
      tooltipTitle={option.helpText}
    >
      {option.label}
      {option.submenu}
    </Option>
  );
};
